import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api'; // Yeni api.js dosyamızı import ettik
import getBookCoverUrl from '../utils/getBookCoverUrl';
import '../styles/DashBoard.css';

const BookPreview = ({ book, onSelect }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    const rect = e.target.getBoundingClientRect();
    const isButton = e.clientY > (rect.top + rect.height - 40); // Butonun yaklaşık yüksekliği

    if (isButton) {
      navigate(`/book/${book.book_id}`); // Değişiklik id'yi uyumlu hale getirmek için yapıldı
    } else {
      onSelect(book);
    }
  };

  return (
    <div className="book-preview" onClick={handleClick}>
      <img 
        src={getBookCoverUrl(book.cover_image_url)}
        alt={book.title} 
        onError={(e) => { e.target.onerror = null; e.target.src = getBookCoverUrl(); }}
      />
      <div className="preview-overlay">
        <button className="preview-button">Önizle</button>
      </div>
    </div>
  );
};

const BookModal = ({ book, onClose }) => {
  const navigate = useNavigate();

  const handleReadClick = async () => {
    const token = localStorage.getItem('token');  // Tokeni localStorage'dan alıyoruz
  
    if (!token) {
      console.error("Kullanıcı kimlik doğrulaması yapılmadı. Token bulunamadı.");
      // Kullanıcıyı login sayfasına yönlendirme işlemi veya hata mesajı gösterme mekanizması ekleyin
      return;
    }
  
    try {
      // Kitaba ait bilgileri ve bölümleri getiren API çağrısı
      const response = await api.get(`/books/${book.book_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
     
  
      const chapters = response.data.chapters; // response.data içerisindeki chapters ile destructuring yapılır
  
      if (chapters && chapters.length > 0) {
        const firstChapter = chapters.find(chapter => chapter.chapter_number === 1); 
        // Eğer chapter_number 1 olan bölüm yoksa, ilk bölümün id'si kullanılır
        const firstChapterId = firstChapter ? firstChapter.chapter_id : chapters[0].chapter_id; 
        navigate(`/read/${book.book_id}/${firstChapterId}`);
      } else {
        console.error("Kitabın bölümleri bulunamadı.");
        // Hata durumuyla başa çıkma mekanizması ekleyin (uyarı gösterme, varsayılan bir yönlendirme, vb.)
      }
    } catch (error) {
      console.error("Bölümler yüklenirken bir hata oluştu:", error);
      // Hata durumuyla başa çıkma mekanizması ekleyin (uyarı gösterme, vb.)
    }
  };
  const handleMoreInfoClick = () => {
    navigate(`/book/${book.book_id}`);  // Değişiklik id'yi uyumlu hale getirmek için yapıldı
  };

  return (
    <div className="book-modal-overlay">
      <div className="book-modal">
        <button className="close-button" onClick={onClose}>×</button>
        <div className="book-modal-content">
          <img 
            src={getBookCoverUrl(book.cover_image_url)}
            alt={book.title}
            onError={(e) => { e.target.onerror = null; e.target.src = getBookCoverUrl(); }}
          />
          <div className="book-details">
            <h2>{book.title}</h2>
            <div className="book-meta">
              <span>{book.total_views} görüntülenme</span>
              <span>{book.total_likes} beğeni</span>
            </div>
            <div className="actions">
              <button className="read-button" onClick={handleReadClick}>Okumaya Başla</button>
              <button className="more-info-button" onClick={handleMoreInfoClick}>Daha Fazla Bilgi →</button>
            </div>
            <p>{book.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookShelf = ({ title, books }) => {
  const [selectedBook, setSelectedBook] = useState(null);

  return (
    <div className="book-shelf">
      <h2>{title}</h2>
      <div className="book-list">
        {books.slice(0, 6).map((book) => (
          <BookPreview key={book.book_id} book={book} onSelect={setSelectedBook} />
        ))}
      </div>
      {selectedBook && (
        <BookModal 
          book={selectedBook} 
          onClose={() => setSelectedBook(null)} 
        />
      )}
    </div>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({ yourLibrary: [], recommended: [], newReleases: [], popular: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await api.get('/dashboard/dashboard', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const enrichedData = {
          ...response.data,
          yourLibrary: response.data.yourLibrary.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          recommended: response.data.recommended.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          newReleases: response.data.newReleases.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          popular: response.data.popular.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
        };

        setDashboardData(enrichedData);
        setLoading(false);
      } catch (error) {
        console.error('Dashboard verileri alınırken hata oluştu:', error.response?.data ?? error.message);
        setError(`Dashboard verileri alınırken hata oluştu: ${error.response?.data ?? error.message}`);
        setLoading(false);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      }
    };

    fetchDashboardData();
  }, [navigate]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="book-dashboard">
      <h1>Yeni Favori Hikayeni Bul</h1>
      <BookShelf title="Kütüphaneniz" books={dashboardData.yourLibrary} />
      <BookShelf title="Sizin İçin Önerilen" books={dashboardData.recommended} />
      <BookShelf title="Yeni Çıkanlar" books={dashboardData.newReleases} />
      <BookShelf title="Popüler Kitaplar" books={dashboardData.popular} />
    </div>
  );
};

export default Dashboard;